<template>
  <div class="step-inner-padded" data-testid="oc-details-step">
    <div class="details-step-container" data-testid="oc-details-step-container">
      <h2 class="step-title">{{ $t('order_creation.details_step.main_heading') }}</h2>
      <p class="step-subtitle">{{ $t('order_creation.details_step.description') }}</p>

      <LeadAlert
        v-if="budgetGateActive"
        variant="error"
        :title="$t('order_creation.details_step.no_active_budget_title')"
        class="tw:mb-5">
        {{ $t('order_creation.details_step.no_active_budget_description') }}
      </LeadAlert>

      <div class="cards-container" :class="{ 'cards-disabled': budgetGateActive }" :aria-disabled="budgetGateActive">
        <!-- Basic Card -->
        <OrderDetailsBasicCard
          :is-open="openCard === 'basic'"
          :completed="completedCards.has('basic')"
          :has-error="!!errors.basic"
          :errors="errors"
          :saving="savingCard === 'basic'"
          @toggle="handleCardToggle('basic')"
          @save="saveCard('basic')"
          @enter-pressed="saveCard('basic')" />

        <!-- Company Card -->
        <OrderDetailsCompanyCard
          :is-open="openCard === 'company'"
          :completed="completedCards.has('company')"
          :has-error="!!errors.company"
          :error="errors.company"
          :saving="savingCard === 'company'"
          @toggle="handleCardToggle('company')"
          @save="saveCard('company')"
          @enter-pressed="saveCard('company')" />

        <!-- Location Card -->
        <OrderDetailsLocationCard
          ref="locationCardRef"
          :is-open="openCard === 'location'"
          :completed="completedCards.has('location')"
          :has-error="!!errors.location"
          :error="errors.location"
          :saving="savingCard === 'location'"
          @toggle="handleCardToggle('location')"
          @save="saveCard('location')"
          @enter-pressed="saveCard('location')"
          @map-loaded="onLocationMapLoaded" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useAutopilotWizardStep } from '@/composables/useAutopilotWizardStep';
import { ref, reactive, watch, onMounted, nextTick, computed } from 'vue';
import LeadAlert from '@/components/lead-design/LeadAlert.vue';
import OrderDetailsBasicCard from '@/components/orders/creation/subcomponents/OrderDetailsBasicCard.vue';
import OrderDetailsCompanyCard from '@/components/orders/creation/subcomponents/OrderDetailsCompanyCard.vue';
import OrderDetailsLocationCard from '@/components/orders/creation/subcomponents/OrderDetailsLocationCard.vue';
import { useOrderCreationStore } from '@/stores/orderCreation';
import { useActiveBudgetStore } from '@/stores/activeBudget';
import { useCustomerFeaturesStore } from '@/stores/customerFeatures';
import translator from '@/locale/translator';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';

const router = useRouter();

const activeBudgetStore = useActiveBudgetStore();
const { noActiveBudget, budgetGateActive } = storeToRefs(activeBudgetStore);

const store = useOrderCreationStore();
const { order } = storeToRefs(store);

const featureStore = useCustomerFeaturesStore();
const { featureBoundByBudget } = storeToRefs(featureStore);

const allowNext = defineModel<boolean>('allowNext');

/* -------------------------------- AUTOPILOT ------------------------------- */
useAutopilotWizardStep(() => ({
  key: 'details',
  title: translator.get('order_creation.details_step.main_heading'),
  description:
    'The user checks and completes the job details: title, employment type, workload, company and location. The values are in context.current_order.',
}));
const emit = defineEmits<{ ready: [] }>();

type CardKey = 'basic' | 'company' | 'location';
const cards = ['basic', 'company', 'location'] as CardKey[];

const locationCardRef = ref<InstanceType<typeof OrderDetailsLocationCard>>();
const openCard = ref<CardKey | null>(null);
const completedCards = ref<Set<CardKey>>(new Set());
const errors = reactive<Record<string, string>>({});

const savingCard = ref<CardKey | null>(null); // which card is being saved / null when no save in progress
const mountComplete = ref(false);
const mapLoaded = ref(false);
const isReady = computed(() => mountComplete.value && mapLoaded.value);

// When the customer is bound_by_budget but has no active budget record, the backend
// rejects draft creation with a validation error. Pre-empt the request, show an inline
// notice, and prevent the user from entering data that cannot be saved.
async function checkActiveBudgetGate() {
  if (!featureBoundByBudget.value) return;
  // Only gate new-draft creation — existing drafts can still be edited.
  if (router.currentRoute.value.query.orderId) return;

  await activeBudgetStore.waitForActiveBudgetLoading();

  if (noActiveBudget.value) {
    openCard.value = null;
    allowNext.value = false;
  }
}

onMounted(async () => {
  try {
    await checkActiveBudgetGate();
    if (budgetGateActive.value) return;

    if (validateCard('company', false)) completedCards.value.add('company');

    const splitStep = (router.currentRoute.value.query.step as string)?.split(':');
    const urlCard = cards.includes(splitStep?.[1] as CardKey) ? (splitStep[1] as CardKey) : null;

    if (validateCard('basic', false)) {
      completedCards.value.add('basic');
      // wait for the initial geocoding to settle so that location fields are
      // in the store before the draft creation request captures order state
      await locationCardRef.value?.geocodingReady;
      await saveCard('basic', { silent: true });
    }

    // honor an explicit URL target; otherwise open the first card with missing details,
    // leaving every card closed when all details were crawled successfully
    openCard.value = urlCard ?? cards.find((c) => !completedCards.value.has(c)) ?? null;
  } finally {
    mountComplete.value = true;
  }
});

/* --------------------------------- METHODS -------------------------------- */
function onLocationMapLoaded() {
  mapLoaded.value = true;
  if (budgetGateActive.value) return;
  if (validateCard('location', false)) completedCards.value.add('location');
}

const handleCardToggle = (card: CardKey) => {
  if (budgetGateActive.value) return;
  openCard.value = openCard.value === card ? null : card;
};

const validateCard = (card: CardKey, showError = true): boolean => {
  let isValid = true;
  delete errors[card];

  if (card === 'basic') {
    delete errors.workload_min;
    delete errors.workload_max;

    if (!order.value.title) {
      if (showError) errors.basic = translator.get('order_creation.details_step.job_title_required');
      isValid = false;
    }

    if (!order.value.workload_fulltime) {
      const workloadMin = Number(order.value.workload_min);
      const workloadMax = Number(order.value.workload_max);

      if (Number.isFinite(workloadMin) && Number.isFinite(workloadMax) && workloadMin > workloadMax) {
        if (showError) {
          const errorMessage = translator.get('order_creation.details_step.workload_min_must_be_smaller_or_equal_max');
          errors.workload_min = errorMessage;
          errors.basic = errorMessage;
        }
        isValid = false;
      }
    }
  } else if (card === 'company') {
    if (!order.value.company_name) {
      if (showError) errors.company = translator.get('order_creation.details_step.company_required');
      isValid = false;
    }
  } else if (card === 'location') {
    if (!order.value.workplace_address) {
      if (showError) errors.location = translator.get('order_creation.details_step.address_required');
      isValid = false;
    }
  }
  return isValid;
};

async function saveCard(card: CardKey, options: { silent?: boolean } = {}) {
  if (budgetGateActive.value) return;
  if (savingCard.value) return; // guard against double-submit while a save is in flight
  if (!validateCard(card)) {
    completedCards.value.delete(card);
    return;
  }
  if (!options.silent) savingCard.value = card;
  try {
    if (!router.currentRoute.value.query.orderId && (order.value.title?.trim().length || 0) > 2) {
      await store.generateDraft();
    } else {
      await store.updateDraft();
    }

    completedCards.value.add(card);

    // timeout to allow users to see the saving state before moving to the next card (not functional otherwise)
    if (!options.silent) await new Promise((r) => setTimeout(r, 400));

    const currentIndex = cards.indexOf(card);
    const nextCard = cards[currentIndex + 1] || null;
    let stepSuffix: CardKey | null;

    if (options.silent) {
      stepSuffix = card;
    } else {
      openCard.value = nextCard;
      stepSuffix = nextCard;
    }

    nextTick(() =>
      router.replace({
        query: {
          ...router.currentRoute.value.query,
          step: `details${stepSuffix ? `:${stepSuffix}` : ''}`,
          orderId: order.value.id,
        },
      })
    );
  } catch (e: any) {
    if (e?.responseJson?.errors) Object.assign(errors, e.responseJson.errors);
    // a failed save means the card is not complete, so it (or the first other
    // incomplete card) gets opened by the "first non-completed card" logic
    completedCards.value.delete(card);
    // this is used to cause the card border to get red
    errors[card] = translator.get('order_creation.details_step.job_title_required');
  } finally {
    savingCard.value = null;
  }
}

/* ---------------------------------- WATCH --------------------------------- */
watch(
  completedCards,
  (newVal) => {
    allowNext.value = newVal.size === 3;
  },
  { deep: true }
);

watch(isReady, (ready) => {
  if (ready) emit('ready');
});
</script>
<style scoped lang="scss">
* {
  font-family: var(--font-brand);
}

.details-step-container {
  max-width: 558px;
  width: 558px;
  margin: 0 auto;
  background-color: #ffffff;
  padding: 30px;
  border-radius: 10px;
  border: 1px solid#EAECF0;
}

.step-title {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 8px;
}

.step-subtitle {
  font-size: 14px;
  color: #475467;
  margin-bottom: 24px;
}

.cards-container {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &.cards-disabled {
    pointer-events: none;
    opacity: 0.5;
    user-select: none;
  }
}
</style>
